import { DIVISIONS } from '@multirx/shared/types/division.js';
import type { DivisionId } from '@multirx/shared/types/division.js';
import type {
  SeasonCampaignDetail,
  SeasonDriverPointsRow,
} from '@multirx/shared/types/championship.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { el } from '../ui/dom.js';
import { bindStandingsDriverTooltips } from '../standings/standingsDriverTooltip.js';
import { bindStandingsPointsBreakdownTooltips } from '../standings/standingsPointsBreakdownTooltip.js';
import {
  standingsPointsColumnsHeadHtml,
  standingsPointsColumnsHtml,
  standingsRankCellHtml,
  standingsRowClass,
} from '../standings/standingsTableMarkup.js';
import {
  readStoredStandingsDivision,
  storeStandingsDivision,
} from '../standings/standingsDivisionPrefs.js';
import type { StandingsController } from '../standings/StandingsApp.js';

export function mountCampaignStandingsEmbed(
  root: HTMLElement,
  detail: SeasonCampaignDetail
): StandingsController {
  root.innerHTML = '';
  root.className = 'seasons-standings-embed seasons-standings-embed--compact';

  let selectedDivisionId = readStoredStandingsDivision();
  if (!detail.divisions.some((d) => d.division === selectedDivisionId)) {
    selectedDivisionId = detail.divisions[0]?.division ?? 1;
  }

  const tabsContainer = el('div', {
    class: 'seasons-standings-div-tabs',
    role: 'tablist',
  });
  const content = el('div', { class: 'seasons-standings-content' });
  root.append(tabsContainer, content);
  bindStandingsDriverTooltips(content);
  bindStandingsPointsBreakdownTooltips(content);

  function renderTabs(): void {
    tabsContainer.innerHTML = '';
    for (const div of DIVISIONS) {
      if (!detail.divisions.some((d) => d.division === div.id)) continue;
      const tab = el('button', {
        type: 'button',
        class: `seasons-standings-div-tab${div.id === selectedDivisionId ? ' is-active' : ''}`,
        text: `Division ${div.id}`,
      });
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', div.id === selectedDivisionId ? 'true' : 'false');
      tab.addEventListener('click', () => selectDivision(div.id));
      tabsContainer.appendChild(tab);
    }
  }

  function renderTable(
    rows: SeasonDriverPointsRow[],
    seasonId: number,
    division: DivisionId
  ): void {
    content.innerHTML = '';
    if (rows.length === 0) {
      content.appendChild(
        el('p', {
          class: 'seasons-detail-empty',
          text: 'Aucun point attribué pour cette division.',
        })
      );
      return;
    }
    const wrap = el('div', { class: 'standings-board seasons-standings-board data-table-wrap' });
    const table = el('table', {
      class: 'seasons-standings-table data-table data-table--standings',
      role: 'table',
    });
    const thead = el('thead');
    thead.innerHTML = `
      <tr>
        <th scope="col" class="col-rank">Rang</th>
        <th scope="col">Pilote</th>
        <th scope="col" class="col-flag" aria-label="Pays">Pays</th>
        ${standingsPointsColumnsHeadHtml()}
      </tr>`;
    const tbody = el('tbody');
    for (const row of rows) {
      const tr = el('tr', { class: standingsRowClass(row.rank) });
      tr.dataset.driverId = String(row.driverId);
      tr.dataset.driverName = row.driverName;
      tr.dataset.standingsRank = String(row.rank);
      tr.dataset.standingsPoints = String(row.points);
      tr.dataset.division = String(division);
      tr.dataset.seasonId = String(seasonId);
      tr.tabIndex = 0;
      tr.innerHTML = `
        <td class="col-rank">${standingsRankCellHtml(row.rank)}</td>
        <td class="col-driver">${labelOwnDriver(row.driverName, row.driverId)}</td>
        <td class="col-flag">${nationalityFlagMarkup('')}</td>
        ${standingsPointsColumnsHtml(
          row.points,
          row.qualPoints ?? 0,
          row.coursePoints ?? 0,
          row.rank,
          {
            seasonId,
            driverId: row.driverId,
          }
        )}`;
      tbody.appendChild(tr);
    }
    table.append(thead, tbody);
    wrap.appendChild(table);
    content.appendChild(wrap);
  }

  function loadDivision(divisionId: number): void {
    const div = detail.divisions.find((d) => d.division === divisionId);
    if (!div) {
      content.innerHTML = '<p class="seasons-detail-empty">Division introuvable.</p>';
      return;
    }
    renderTable(div.standings, div.seasonId, div.division);
  }

  function selectDivision(divisionId: number): void {
    if (divisionId < 1 || divisionId > 4) return;
    if (divisionId === selectedDivisionId) {
      loadDivision(divisionId);
      return;
    }
    selectedDivisionId = divisionId;
    storeStandingsDivision(divisionId);
    renderTabs();
    loadDivision(divisionId);
  }

  renderTabs();
  loadDivision(selectedDivisionId);

  return {
    selectDivision,
    getSelectedDivision: () => selectedDivisionId,
    refresh: () => loadDivision(selectedDivisionId),
  };
}
